import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { telematicsApi, type DecodedRecordView, type RawFrameSummary } from '../../api/telematics';
import { AsyncBoundary } from '../../ui/AsyncBoundary';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { DataTable } from '../../ui/DataTable';
import { Modal } from '../../ui/Modal';
import { inputClass } from '../../ui/Field';
import { formatInstant } from '../../ui/datetime';

/**
 * Rohdaten zur Fehlersuche.
 *
 * Die Ansicht hat einen Zweck: nachsehen, was das Gerät tatsächlich geschickt
 * hat — nicht, was das Datenblatt verspricht. Deshalb stehen hier bewusst auch
 * der unlesbare Frame samt Fehlermeldung und der rohe Hexdump. Beides sucht
 * man genau dann, wenn etwas nicht stimmt.
 *
 * Nur für die Administration: Ein Rohframe trägt unredigierte Positionen, und
 * jeder Aufruf landet im Betriebsprotokoll.
 */
export function RawTelemetryPage() {
  const [imei, setImei] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);

  const devices = useQuery({ queryKey: ['telematics-devices'], queryFn: telematicsApi.devices });

  const query = useQuery({
    queryKey: ['telemetry-raw', { imei }],
    queryFn: () => telematicsApi.raw({ imei: imei || undefined, limit: 100 }),
    // Beim Debuggen will man sehen, was gerade hereinkommt.
    refetchInterval: 10_000,
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">Rohdaten</h2>
        <p className="text-sm text-slate-500">
          Was die Geräte tatsächlich senden, Frame für Frame. Gedacht für die Fehlersuche —
          Positionen stehen hier unredigiert, und jeder Aufruf wird protokolliert.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={imei}
          onChange={(event) => setImei(event.target.value)}
          className={`${inputClass} max-w-xs`}
          aria-label="Nach Gerät filtern"
        >
          <option value="">Alle Geräte</option>
          {devices.data?.map((device) => (
            <option key={device.id} value={device.imei}>
              {device.imei}
              {device.status === 'ACTIVE' ? '' : ` (${device.status})`}
            </option>
          ))}
        </select>
        <span className="text-xs text-slate-500">aktualisiert sich alle 10 Sekunden</span>
      </div>

      <AsyncBoundary
        query={query}
        empty="Noch keine Rohdaten empfangen. Sobald ein Gerät sendet, erscheinen die Frames hier."
      >
        {(rows) => (
          <DataTable
            rows={rows}
            rowKey={(row) => String(row.id)}
            rowClassName={(row) => (row.parseError ? 'bg-red-50' : '')}
            columns={[
              {
                header: 'Empfangen',
                cell: (row) => (
                  <span className="tabular-nums">{formatInstant(row.receivedAt, 'medium')}</span>
                ),
              },
              {
                header: 'Gerät',
                cell: (row) => <span className="font-mono text-xs">{row.imei}</span>,
              },
              {
                header: 'Codec',
                cell: (row) => (row.codecId ? `0x${row.codecId.toString(16)}` : '—'),
              },
              { header: 'Records', align: 'right', cell: (row) => row.recordCount ?? '—' },
              { header: 'Bytes', align: 'right', cell: (row) => row.bytes },
              { header: 'Zustand', cell: (row) => <FrameState row={row} /> },
              {
                header: '',
                align: 'right',
                cell: (row) => (
                  <Button size="sm" variant="ghost" onClick={() => setOpenId(row.id)}>
                    Ansehen
                  </Button>
                ),
              },
            ]}
          />
        )}
      </AsyncBoundary>

      {openId !== null && <FrameDialog id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function FrameState({ row }: { row: RawFrameSummary }) {
  if (row.parseError) return <Badge tone="red">nicht lesbar</Badge>;

  return (
    <div className="flex flex-wrap gap-1">
      {row.crcOk === false && <Badge tone="red">CRC falsch</Badge>}
      {row.anonymization !== 'NONE' && <Badge tone="amber">anonymisiert</Badge>}
      {!row.processedAt && <Badge tone="neutral">unverarbeitet</Badge>}
    </div>
  );
}

function FrameDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['telemetry-raw', id],
    queryFn: () => telematicsApi.rawDetail(id),
  });

  return (
    <Modal title={`Rohsatz #${id}`} onClose={onClose}>
      <AsyncBoundary query={query}>
        {(frame) => (
          <div className="space-y-4">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <Fact label="Gerät" value={frame.imei} mono />
              <Fact label="Herkunft" value={frame.source} />
              <Fact label="Empfangen" value={formatInstant(frame.receivedAt, 'medium')} />
              <Fact label="Codec" value={frame.codecId ? `0x${frame.codecId.toString(16)}` : '—'} />
              <Fact label="Größe" value={`${frame.bytes} Byte`} />
              <Fact label="Prüfsumme" value={frame.crcOk === false ? 'falsch' : 'stimmt'} />
            </dl>

            {frame.parseError && (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
                {frame.parseError}
              </p>
            )}

            {frame.records.map((record, index) => (
              <RecordView key={index} record={record} index={index} />
            ))}

            {/*
              Der rohe Frame zum Schluss und eingeklappt: Man braucht ihn selten,
              aber wenn der Parser irrt, ist er die einzige Wahrheit.
            */}
            <details className="rounded-md border border-slate-200">
              <summary className="cursor-pointer px-3 py-2 text-sm text-slate-600">
                Frame in Hexadezimal
              </summary>
              <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-all px-3 pb-3 font-mono text-xs text-slate-500">
                {frame.hex}
              </pre>
            </details>
          </div>
        )}
      </AsyncBoundary>
    </Modal>
  );
}

function RecordView({ record, index }: { record: DecodedRecordView; index: number }) {
  const mapUrl = `https://www.openstreetmap.org/?mlat=${record.latitude}&mlon=${record.longitude}#map=17/${record.latitude}/${record.longitude}`;

  return (
    <div className="rounded-md border border-slate-200">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
        <span className="text-xs font-medium text-slate-500">Record {index + 1}</span>
        <span className="text-sm tabular-nums">{formatInstant(record.recordedAt, 'medium')}</span>
        {record.eventLabel && <Badge tone="blue">{record.eventLabel}</Badge>}
        {!record.hasFix && <Badge tone="amber">ohne Fix</Badge>}
      </div>

      <div className="px-3 py-2 text-sm">
        {record.hasFix ? (
          <p className="tabular-nums">
            {record.latitude.toFixed(5)}, {record.longitude.toFixed(5)} · {record.satellites} Sat ·{' '}
            {record.speedKmh} km/h · {record.altitudeM} m
            <a
              className="ml-2 text-slate-500 underline hover:text-slate-900"
              href={mapUrl}
              target="_blank"
              rel="noreferrer"
            >
              Karte
            </a>
          </p>
        ) : (
          <p className="text-slate-500">
            Keine gültige Position — das Gerät sendet auch ohne Satellitenempfang.
          </p>
        )}

        {record.io.length > 0 && (
          <dl className="mt-2 space-y-0.5">
            {record.io.map((value) => (
              <div key={value.id} className="flex gap-2">
                <dt className="w-8 shrink-0 text-right font-mono text-xs text-slate-400">
                  {value.id}
                </dt>
                <dd className="flex-1">
                  {/* Unbekannte IDs blass — sie stehen nicht in der Herstellertabelle. */}
                  <span className={value.known ? '' : 'text-slate-400'} title={value.group}>
                    {value.label}
                  </span>
                  <span className="ml-2 tabular-nums">
                    {value.value}
                    {value.unit ? ` ${value.unit}` : ''}
                  </span>
                  {value.multiplierNote && (
                    <span className="ml-2 text-xs text-amber-700">
                      Multiplikator {value.multiplierNote} — nicht angewandt
                    </span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <dt className="text-slate-400">{label}:</dt>
      <dd className={mono ? 'font-mono text-xs' : ''}>{value}</dd>
    </div>
  );
}

